import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, MessageSquare, Clock, Globe, Lock, ArrowRight, ChevronLeft, Shield, Mic2, Users, Camera, Image as ImageIcon, Key, Calendar, Search, Loader, Info, X } from 'lucide-react';
import { cn, getRoomPath } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { useAuth } from '../context/AuthContext';
import { useCreation } from '../hooks/useCreation';
import { themeClasses } from '../utils/themes';

const LiveRoomCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const community_id = searchParams.get('community_id');
  const { addRoom } = useActivities();

  const theme = themeClasses['qa'];
  const speakerSearchAllowed = Boolean(community_id || user?.role === 'platform_admin');

  const {
    step,
    setStep,
    loading,
    setLoading,
    error,
    setError,
    image: roomImage,
    fileInputRef,
    handleFileChange,
    speakers,
    speakerInput,
    setSpeakerInput,
    addSpeaker,
    removeSpeaker,
    searchMembers,
    searchResults,
    searching,
    nextStep: triggerNextStep,
    prevStep,
  } = useCreation();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (speakerInput.trim().length >= 2 && speakerSearchAllowed) {
        searchMembers(speakerInput, community_id, user?.role === 'platform_admin');
      } else {
        searchMembers('', community_id, speakerSearchAllowed);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [speakerInput, community_id, searchMembers, speakerSearchAllowed, user?.role]);

  const [privacy, setPrivacy] = useState('public');
  const [password, setPassword] = useState('');
  const [inviteOnly, setInviteOnly] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timeframe, setTimeframe] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState('1h');

  const handleCreate = async () => {
    const validate = () => {
      if (!title.trim()) return 'Please enter a room title';
      if (timeframe === 'scheduled' && !scheduledAt) return 'Please select a scheduled date and time';
      return null;
    };

    const errorMsg = validate();
    if (errorMsg) {
      setError(errorMsg);
      if (!title.trim()) setStep(1);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const newRoom = await addRoom({
        title,
        description,
        type: 'Q&A',
        privacy,
        settings: {
          password: privacy === 'private' ? password : null,
          invite_only: privacy === 'private' ? inviteOnly : false,
          speakers: speakers.length > 0 ? speakers.map(s => s.name) : null,
          duration: duration // Put duration explicitly in settings
        },
        image: roomImage,
        community_id,
        timeframe,
        scheduled_at: timeframe === 'scheduled' ? scheduledAt : null,
        participants: 0,
        host: user?.name || 'You'
      });
      navigate(getRoomPath(newRoom));
    } catch (err) {
      console.error('Room creation failed:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to create room. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    triggerNextStep(() => {
      if (!title.trim()) return 'Please enter a room title';
      return null;
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
      <div className="flex items-center gap-4 mb-10">
        <button 
          onClick={() => step > 1 ? prevStep() : navigate(-1)}
          className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Host a Live Q&A</h1>
          <p className="text-gray-500 font-medium text-sm">Step {step} of 2: {step === 1 ? 'Details' : 'Visibility & Scheduling'}</p>
        </div>
      </div>

      {step === 1 ? (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-8"
        >
          <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 space-y-6 shadow-xl shadow-gray-100/50">
            {error && (
              <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                <Shield size={16} />
                {error}
              </div>
            )}
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
            />
            
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Room Cover Image</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="relative h-48 rounded-3xl bg-gray-50 border-2 border-dashed border-gray-100 flex flex-col items-center justify-center cursor-pointer group hover:bg-gray-100 transition-all overflow-hidden"
              >
                {roomImage ? (
                  <>
                    <img src={roomImage} alt="Room cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera size={32} className="text-white" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 rounded-full bg-white shadow-sm mb-2 text-gray-300 group-hover:text-brand-indigo transition-colors">
                      <ImageIcon size={32} />
                    </div>
                    <p className="text-xs font-bold text-gray-400">Click to upload room image</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6 p-6 bg-purple-50/50 rounded-[2rem] border-2 border-purple-50">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-2xl shadow-lg shadow-purple-100 flex items-center justify-center shrink-0">
                <MessageSquare size={32} />
              </div>
              <div className="text-center md:text-left">
                <h4 className="font-bold text-purple-900 text-lg">Interactive Q&A Session</h4>
                <p className="text-sm text-purple-700/80 font-medium leading-relaxed max-w-md">
                  A dedicated space for structured discussions. Enable upvoting, pin featured questions, and manage speakers effortlessly.
                </p>
              </div>
              <div className="flex -space-x-2 md:ml-auto">
                {[1,2,3].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-purple-200 flex items-center justify-center">
                    <Users size={12} className="text-purple-600" />
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full border-2 border-white bg-purple-600 flex items-center justify-center text-[10px] font-bold text-white">
                  +
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Room Title</label>
              <input 
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Ask Me Anything: Web3 Engineering"
                className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-purple-500/20 font-bold transition-all text-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Speaker(s)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                <div className={cn("px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 border", theme.lightBg, theme.text, theme.lightBorder)}>
                  <Mic2 size={14} />
                  You (Host)
                </div>
                {speakers.map((speaker) => (
                  <div key={speaker.id} className="px-4 py-2 bg-gray-50 text-gray-700 rounded-xl font-bold text-sm flex items-center gap-2 border border-gray-100">
                    <div className="w-5 h-5 rounded-full bg-brand-indigo/10 flex items-center justify-center overflow-hidden shrink-0">
                      {speaker.avatar ? <img src={speaker.avatar} alt={speaker.name} className="w-full h-full object-cover" /> : <Users size={12} className="text-brand-indigo" />}
                    </div>
                    {speaker.name}
                    <button onClick={() => removeSpeaker(speaker.id)} className="ml-1 hover:text-red-500 transition-colors font-bold text-gray-400">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="relative">
                <div className="relative">
                  <input 
                    type="text" 
                    value={speakerInput}
                    onChange={(e) => setSpeakerInput(e.target.value)}
                    placeholder={community_id ? "Search community members..." : speakerSearchAllowed ? "Search platform members..." : "Only platform admins can assign speakers outside a community"}
                    disabled={!speakerSearchAllowed}
                    className={cn(
                      "w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-xl font-medium text-sm transition-all focus:ring-2",
                      theme.ring,
                      !speakerSearchAllowed && "cursor-not-allowed opacity-60"
                    )}
                  />
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {searching ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
                  </div>
                </div>

                <AnimatePresence>
                  {searchResults.length > 0 && speakerInput.trim().length >= 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto"
                    >
                      {searchResults.map((member) => (
                        <button
                          key={member.id}
                          onClick={() => addSpeaker(member)}
                          className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-brand-indigo/5 flex items-center justify-center overflow-hidden shrink-0">
                            {member.avatar ? <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" /> : <Users size={16} className="text-brand-indigo" />}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-gray-900">{member.name}</p>
                            <p className="text-xxs font-medium text-gray-500">{member.email}</p>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <p className="text-xxs font-medium text-gray-400 mt-2 flex items-center gap-1.5 px-1">
                <Info size={10} />
                {community_id
                  ? 'Speakers must be current members of this community.'
                  : speakerSearchAllowed
                    ? 'Outside a community, only platform admins can assign additional speakers.'
                    : 'This room is not linked to a community, so only platform admins may add speakers.'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Description</label>
              <textarea 
                rows="3"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this AMA about?"
                className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-purple-500/20 font-medium transition-all resize-none"
              ></textarea>
            </div>
          </div>

          <button 
            onClick={nextStep}
            className="w-full bg-purple-600 text-white font-bold py-5 rounded-2xl shadow-xl shadow-purple-200 flex items-center justify-center gap-2 hover:bg-purple-700 transition-all text-lg"
          >
            Continue
            <ArrowRight size={20} />
          </button>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                  <Shield size={16} />
                  {error}
                </div>
              )}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Timeframe</h2>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => setTimeframe('now')}
                    className={cn(
                      "flex flex-col items-center justify-center p-6 rounded-3xl border-2 transition-all",
                      timeframe === 'now' ? "bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-100" : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Radio size={24} className={cn("mb-2", timeframe === 'now' && "animate-pulse")} />
                    <span className="font-bold text-sm">Start Now</span>
                  </button>
                  <button 
                    onClick={() => setTimeframe('scheduled')}
                    className={cn(
                      "flex flex-col items-center justify-center p-6 rounded-3xl border-2 transition-all",
                      timeframe === 'scheduled' ? "bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-100" : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Clock size={24} className="mb-2" />
                    <span className="font-bold text-sm">Schedule</span>
                  </button>
                </div>

                {timeframe === 'scheduled' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 space-y-3"
                  >
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar size={14} />
                      <label className="text-xxs font-bold uppercase tracking-widest">Schedule Date & Time</label>
                    </div>
                    <input 
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      className="w-full px-5 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-purple-500/10 font-bold text-sm transition-all"
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </motion.div>
                )}
              </section>

              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Expected Duration</h2>
                <div className="grid grid-cols-3 gap-2">
                  {['30m', '1h', '2h'].map((d) => (
                    <button 
                      key={d} 
                      onClick={() => setDuration(d)}
                      className={cn(
                        "py-3 rounded-xl font-bold transition-all text-sm",
                        duration === d ? "bg-purple-600 text-white shadow-md shadow-purple-100" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </section>
            </div>

            <div className="space-y-6">
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Accessibility</h2>
                <div className="space-y-3">
                  <button 
                    onClick={() => setPrivacy('public')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                      privacy === 'public' ? "border-purple-600 bg-purple-50" : "border-gray-50 bg-white"
                    )}
                  >
                    <Globe size={20} className={privacy === 'public' ? "text-purple-600" : "text-gray-400"} />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Public AMA</p>
                      <p className="text-xxs text-gray-500 font-medium">Anyone can join and ask questions</p>
                    </div>
                  </button>
                  <button 
                    onClick={() => setPrivacy('private')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                      privacy === 'private' ? "border-purple-600 bg-purple-50" : "border-gray-50 bg-white"
                    )}
                  >
                    <Lock size={20} className={privacy === 'private' ? "text-purple-600" : "text-gray-400"} />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Private / Community Only</p>
                      <p className="text-xxs text-gray-500 font-medium">Only specific members or links</p>
                    </div>
                  </button>
                </div>
              </section>

              {privacy === 'private' && (
                <motion.section
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 pt-2"
                >
                  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Private Access Configuration</h2>
                  <div className="bg-white rounded-3xl border border-purple-100 p-6 space-y-5 shadow-sm">
                    <button 
                      onClick={() => setInviteOnly(!inviteOnly)}
                      className="w-full flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn("p-2.5 rounded-xl transition-colors", inviteOnly ? "bg-purple-100 text-purple-600" : "bg-gray-50 text-gray-400")}>
                          <Shield size={18} />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm text-gray-900">Community Only</p>
                          <p className="text-xxs text-gray-500 font-medium uppercase tracking-wider">Restrict access to members</p>
                        </div>
                      </div>
                      <div className={cn("w-12 h-7 rounded-full relative transition-colors p-1", inviteOnly ? "bg-purple-600" : "bg-gray-200")}>
                        <div className={cn("w-5 h-5 bg-white rounded-full transition-all shadow-sm", inviteOnly ? "translate-x-5" : "translate-x-0")}></div>
                      </div>
                    </button>

                    <div className="h-px bg-gray-50 w-full"></div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Key size={14} />
                        <label className="text-xxs font-bold uppercase tracking-widest">Access Password</label>
                      </div>
                      <input 
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Optional password..."
                        className="w-full px-5 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-purple-500/10 font-bold text-sm transition-all"
                      />
                    </div>
                  </div>
                </motion.section>
              )}
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-100 rounded-3xl p-6">
            <div className="flex gap-4">
              <div className="p-3 bg-white rounded-2xl text-purple-600 shadow-sm">
                <Shield size={20} />
              </div>
              <div>
                <h4 className="font-bold text-purple-900 text-sm">Moderation Suite</h4>
                <p className="text-xs text-purple-700 font-medium leading-relaxed mt-0.5">
                  Full control over question filtering, speaker management, and audience participation.
                </p>
              </div>
            </div>
          </div>

          <button 
            onClick={handleCreate}
            disabled={loading}
            className={cn(
              "w-full bg-purple-600 text-white font-bold py-5 rounded-2xl shadow-xl shadow-purple-200 flex items-center justify-center gap-3 hover:bg-purple-700 transition-all text-xl disabled:opacity-50 disabled:cursor-not-allowed mt-10",
              loading && "opacity-70 cursor-not-allowed"
            )}
          >
            {loading ? (
              <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Radio size={24} className={timeframe === 'now' ? 'animate-pulse' : ''} />
                {timeframe === 'now' ? 'Go Live Now' : 'Schedule Room'}
              </>
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default LiveRoomCreation;
